import React from 'react';
import { FiX } from 'react-icons/fi';
import { motion } from 'framer-motion';

export default function ServiceDetail({ service, onClose }) {
  if (!service) return null;

  const serviceDetails = {
    'Strategic Growth': {
      description: 'Data-driven strategies to scale your brand presence across all social media platforms.',
      detailed: 'Our strategic growth service focuses on sustainable, organic expansion of your social media presence. We analyze your current metrics, identify growth opportunities, and implement proven strategies to increase followers, engagement, and brand awareness across all platforms.',
      features: [
        'Audience analysis and targeting',
        'Content strategy development',
        'Growth hacking techniques',
        'Platform optimization',
        'Monthly performance reports',
      ],
    },
    'Influence & Authority': {
      description: 'Build thought leadership and brand credibility through strategic influencer partnerships.',
      detailed: 'Establish your brand as an industry authority through strategic influencer collaborations. We identify and partner with influencers aligned with your brand values, manage campaigns, and track measurable ROI to ensure every partnership drives real business results.',
      features: [
        'Influencer identification',
        'Collaboration management',
        'Content creation guidance',
        'Audience engagement tracking',
        'Brand authority building',
      ],
    },
    'Smart Strategy': {
      description: 'Creative campaigns that turn engagement into conversions with data-backed creativity.',
      detailed: 'Transform your engagement into measurable conversions with our smart strategy service. We combine creative excellence with data-driven insights to design campaigns that resonate with your audience and drive meaningful business outcomes.',
      features: [
        'Campaign ideation',
        'Creative direction',
        'Performance optimization',
        'A/B testing',
        'Conversion rate optimization',
      ],
    },
    'Community Management': {
      description: 'Engage, nurture, and grow your community with professional management and moderation.',
      detailed: 'Build a thriving community around your brand with expert management and moderation. We engage with your audience, handle customer service, analyze sentiment, and nurture relationships that turn followers into loyal advocates.',
      features: [
        '24/7 community engagement',
        'Customer service excellence',
        'Community growth strategies',
        'Content moderation',
        'Sentiment analysis',
      ],
    },
    'Content Creation': {
      description: 'Professional content creation that resonates with your audience and drives engagement.',
      detailed: 'High-quality content is the foundation of social media success. From stunning photography and videography to compelling copywriting and graphic design, we create content that captures attention, tells your story, and drives engagement.',
      features: [
        'Video production',
        'Photography services',
        'Graphic design',
        'Copywriting',
        'Content calendar planning',
      ],
    },
    'Analytics & Reporting': {
      description: 'Comprehensive analytics and transparent reporting to measure your ROI and success.',
      detailed: 'Data transparency is key to success. We provide comprehensive analytics, detailed monthly reports, competitor analysis, and actionable insights to help you understand what\'s working and optimize your strategy for maximum impact.',
      features: [
        'Real-time dashboards',
        'Monthly performance reports',
        'ROI tracking',
        'Competitor analysis',
        'Actionable insights',
      ],
    },
  };

  const details = serviceDetails[service] || serviceDetails['Strategic Growth'];

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-black dark:bg-gray-900 rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto border border-yellow-400/30"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.3 }}
      >
        <div className="sticky top-0 flex justify-between items-center p-6 bg-gradient-to-r from-yellow-400 to-yellow-500 dark:bg-yellow-500">
          <h2 className="text-2xl font-bold text-black">{service}</h2>
          <button
            onClick={onClose}
            className="text-black hover:scale-110 transition-transform"
            aria-label="Close modal"
          >
            <FiX size={28} />
          </button>
        </div>

        <div className="p-6 text-gray-100 dark:text-gray-200">
          <p className="text-lg mb-4 text-white leading-relaxed font-semibold">{details.description}</p>
          <p className="text-base mb-8 text-gray-300 leading-relaxed">{details.detailed}</p>

          <div className="mb-6">
            <h3 className="text-xl font-bold mb-4 text-yellow-400">Key Features & Details</h3>
            <ul className="space-y-3">
              {details.features.map((feature, idx) => (
                <motion.li
                  key={idx}
                  className="flex items-start text-gray-200 dark:text-gray-300"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <span className="w-2 h-2 bg-yellow-400 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span className="text-base">{feature}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          <motion.button
            onClick={() => {
              const contactSection = document.getElementById('contact');
              contactSection?.scrollIntoView({ behavior: 'smooth' });
              onClose();
            }}
            className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 px-6 rounded-lg transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started with {service}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
